import { firstValueFrom, Observable } from "rxjs";
import { inject, injectable, singleton } from "tsyringe";
import { ExtensionContext } from "../../../common/models/extension-config";
import { Module } from "../../../common/module";
import { CommonModule } from "../../common.module";
import { ApiClient } from "./api.client";
import { ApiResponse } from "./api.response";
import { ErrorTypes, ErrorTypesErrorResponseDTO, type FileResponse, SwaggerException } from "./autogenerated";

@singleton()
export class ApiModule extends Module {
    public name: string = 'ApiModule';
    public acceptedLicenseFlags: string[] = [];
    constructor(@inject(CommonModule) private common: CommonModule) {
        super([common]);
    }
    public shouldRun(context: ExtensionContext): boolean { return false; }
    private client: ApiClient | null = null;
    public async run(): Promise<void> {
        // decide the URL we need to use
        let baseUrl: string;
        if (window.stm.config.routingPatched) {
            if (window.stm.config.isStaticPatch) {
                baseUrl = '/stm';
            } else {
                baseUrl = this.common.combineUrl(window.stm.config.serverUrl, 'stm');
            }
        } else {
            baseUrl = window.stm.config.appUrl.trim();
        }
        if (baseUrl.endsWith('/')) {
            baseUrl = baseUrl.substring(0, baseUrl.length - 1);
        }
        this.client = new ApiClient(baseUrl);
    }
    

    public apiRequest<T>(request: (client: ApiClient) => Promise<T> | Observable<T>): Promise<ApiResponse<T>> {
        const req = request(this.client!);
        let reqPromise: Promise<T>;
        if ((req as Observable<T>).subscribe) {
            reqPromise = firstValueFrom(req as Observable<T>);
        } else {
            reqPromise = req as Promise<T>;
        }
        return reqPromise.then(e => new ApiResponse<T>(true, e, undefined)).catch(e => {
            if (!SwaggerException.isSwaggerException(e)) {
                this.log.e('A generic error occured at API request', e);
                return new ApiResponse<T>(
                  false,
                  undefined,
                  new ErrorTypesErrorResponseDTO({
                    requestId: undefined,
                    error: ErrorTypes.UnhandledError,
                    message: 'An unhandled error occured',
                    details: e
                  }));
              }
              const ex = e as SwaggerException;
              const ret = ex.status === 500?  ex.result as ErrorTypesErrorResponseDTO:  new ErrorTypesErrorResponseDTO({
                requestId: undefined,
                error: ErrorTypes.UnhandledError,
                message: 'An unhandled error occured',
                details: e
              });
              this.log.e('API request failed', ret);
              return new ApiResponse<T>(
                false, undefined, ret
              );
        });
    }
    public async htmlRequest(request: (client: ApiClient) => Promise<FileResponse> | Observable<FileResponse>): Promise<ApiResponse<HTMLElement>> {
        const base = await this.apiRequest(request);
        if (!base.success) {
            return new ApiResponse<HTMLElement>(false, undefined, base.error);
        } else {
            const e = document.createElement('div');
            e.innerHTML = (await base.value?.data.text()) || '';
            return new ApiResponse<HTMLElement>(true, (e.children?.length === 1 ? e.children[0]: e) as HTMLElement, undefined);
        }
    }

    public async blobDataUrlRequest(request: (client: ApiClient) => Promise<FileResponse> | Observable<FileResponse>): Promise<ApiResponse<string>> {
        const base = await this.apiRequest(request);
        if (!base.success) {
            return new ApiResponse<string>(false, undefined, base.error);
        } else {
            const readPromise = new Promise<string>((resolve, reject) => {
                const r = new FileReader();
                r.onloadend = () => resolve(r.result as string);
                r.onerror = reject;
                r.readAsDataURL(base.value.data);
            });
            try {
                return new ApiResponse<string>(true, await readPromise, undefined);
            } catch (e) {
                return new ApiResponse<string>(false, undefined, new ErrorTypesErrorResponseDTO({
                    error: ErrorTypes.UnhandledError,
                    message: 'Failed to read file response on client side'
                }));
            }
        }
    }

}